import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { AspectRatio } from '@lumen5/luminary';
import { z } from 'zod';

import { ApiService, NarrativeThreadEnum, type SceneDesign } from '../../client';
import {
    getDisplayGroupById,
    getSupportedAspectRatios,
} from '../../data/templates/templates-selectors';
import {
    renderSceneDesignsToDataUrls,
    TemplateBundleNotLoadedError,
} from '../rendering/scene-design-render';
import {
    buildStripPlan,
    buildStripPreamble,
    compositeTileStrip,
    fetchImageAsDataUrl,
    normalizeAspectRatio,
    type StripPanel,
} from '../rendering/scene-design-tile-strip';
import type { EnhancedToolResult, ImageContent, TextContent, ToolExecutionContext } from '../types';
import { ConversationTool } from './ConversationTool';
import type { ToolConstructorArgs } from './Tool';

export const listSceneDesignSchema = z.object({
    template_id: z
        .string()
        .optional()
        .describe(
            'Optional template ID to list designs from (e.g., "shamrock", "minimal", "modern"). Use this when the user specifies a template name like "from shamrock template" or "in the modern template". If not provided, uses the currently loaded template.',
        ),
    aspect_ratio: z
        .string()
        .optional()
        .describe(
            'Optional aspect ratio (e.g., "landscape", "square", "portrait"). If not provided, defaults to "landscape" or uses the primary aspect ratio of the loaded template.',
        ),
    narrative_thread: z
        .enum(NarrativeThreadEnum)
        .optional()
        .describe(
            `Which kind of scene designs to list. Use "text" for text-on-media (slide) videos. Use "talking_head" or "voiceover" to include overlay-compatible designs (e.g. lower thirds) when finding a design for ${TOOL_NAMES.addVoiceoverScene} on a talking-head or voiceover scene group. Defaults to text.`,
        ),
});
export type ListSceneDesignArgs = z.infer<typeof listSceneDesignSchema>;

interface ListSceneDesignConstructorArgs extends ToolConstructorArgs {
    narrativeThread?: NarrativeThreadEnum;
}

export class ListSceneDesignTool extends ConversationTool<
    typeof listSceneDesignSchema,
    ListSceneDesignConstructorArgs
> {
    protected override readonly schema = listSceneDesignSchema;
    protected override readonly toolName = TOOL_NAMES.listSceneDesigns;
    protected override readonly toolDescription = `List the available scene designs for a template as rendered preview images. Designs are grouped by display group and returned as tile-strip images (up to 4 designs per strip), each panel labeled with its display name and scene_design_id so you can act on a specific design. Previews require the template to be loaded — when creating a new video, call ${TOOL_NAMES.createBlankVideo} first, then list the designs.`;

    private narrativeThread: NarrativeThreadEnum;
    public constructor(args: ListSceneDesignConstructorArgs) {
        super(args);
        this.narrativeThread = args.narrativeThread ?? NarrativeThreadEnum.TEXT;
    }

    public override getToolDisplayName(): string {
        return 'Listing scene designs';
    }

    public get statusIcon(): string {
        return 'view_list';
    }

    /** One image per design: its saved poster where there is one, otherwise a fresh render. */
    private async getDesignImages(
        templateId: string,
        aspectRatio: AspectRatio,
        designs: SceneDesign[],
    ): Promise<Map<string, string | null>> {
        const images = new Map<string, string | null>();
        await Promise.all(
            designs.map(async (design) => {
                const posterUrl = design[`${aspectRatio}_poster_url`];
                if (posterUrl) {
                    images.set(design.id, await fetchImageAsDataUrl(posterUrl));
                }
            }),
        );

        const designIdsToRender = designs
            .map((design) => design.id)
            .filter((sceneDesignId) => !images.get(sceneDesignId));
        if (designIdsToRender.length > 0) {
            const renders = await renderSceneDesignsToDataUrls({
                templateId,
                aspectRatio,
                sceneDesignIds: designIdsToRender,
                getState: this.getState,
            });
            for (const render of renders) {
                images.set(render.sceneDesignId, render.dataUrl);
            }
        }
        return images;
    }

    public async execute(
        args: ListSceneDesignArgs,
        _context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const {
            template_id: providedTemplateId,
            aspect_ratio: providedAspectRatio,
            narrative_thread: providedNarrativeThread,
        } = args;

        // Declare variables outside try block for error handling
        let templateId: string | undefined;
        let aspectRatio: AspectRatio = AspectRatio.LANDSCAPE;
        // An explicit narrative_thread arg (e.g. talking_head for overlay designs) wins;
        // otherwise fall back to the tool's configured default (text in the toolbox).
        const narrativeThread = providedNarrativeThread ?? this.narrativeThread;

        try {
            // Get template ID - use provided template_id or fall back to current luminary template
            if (providedTemplateId) {
                templateId = providedTemplateId;
            } else {
                templateId = this.luminary.template.id;
                if (!templateId) {
                    return {
                        result: 'Error: No template loaded and no template_id provided',
                        isSuccess: false,
                    };
                }
            }

            // Get aspect ratio. Priority:
            // 1. Explicitly provided aspect_ratio.
            // 2. The loaded video's aspect ratio — so the previews match the orientation
            //    the user is actually building in. Without this, a vertical video would
            //    get landscape previews.
            // 3. The template's primary supported aspect ratio (when no video is loaded).
            let rawAspectRatio = 'landscape';
            if (providedAspectRatio) {
                rawAspectRatio = providedAspectRatio;
            } else if (this.luminary.aspectRatio) {
                rawAspectRatio = this.luminary.aspectRatio;
            } else if (this.getState) {
                try {
                    const state = this.getState();
                    const supportedAspectRatios = getSupportedAspectRatios(state, templateId);
                    rawAspectRatio = supportedAspectRatios[0] || 'landscape';
                } catch (error) {
                    console.warn(
                        'Could not get supported aspect ratios, using landscape as default:',
                        error,
                    );
                }
            }
            aspectRatio = normalizeAspectRatio(rawAspectRatio);

            // The annotations endpoint is the source of truth for *which* designs are
            // relevant to the narrative thread (it includes overlay-compatible designs
            // for talking_head/voiceover). We only use it for that filtered id set —
            // the annotation text itself is no longer sent to the model.
            const annotationsResponse = await ApiService.sceneDesignsTemplateAnnotationsRetrieve({
                query: {
                    template_id: templateId,
                    aspect_ratio: aspectRatio,
                    narrative_thread: narrativeThread,
                    // Outro designs are only a valid closing scene for text-on-media videos;
                    // voice-driven groups reject them in isOverlaySceneDesign anyway.
                    include_outros: narrativeThread === NarrativeThreadEnum.TEXT,
                },
            });
            const relevantIds = new Set(
                (annotationsResponse.data?.scene_design_annotations ?? []).map(
                    (item) => item.scene_design_id,
                ),
            );
            if (relevantIds.size === 0) {
                return { result: 'No scene designs found in current template', isSuccess: true };
            }

            // Full design metadata (display_group, display_name, order) for grouping.
            const designsResponse = await ApiService.sceneDesignsList({
                query: { template_id: templateId },
            });
            const designs = (designsResponse.data ?? []).filter((design) =>
                relevantIds.has(design.id),
            );
            if (designs.length === 0) {
                return { result: 'No scene designs found in current template', isSuccess: true };
            }

            const state = this.getState();
            const getGroupName = (groupId: number): string =>
                getDisplayGroupById(state, groupId)?.get('display_name') ?? '';

            const plan = buildStripPlan(designs, aspectRatio, getGroupName);

            const renderMap = await this.getDesignImages(templateId, aspectRatio, designs);

            const resultContent: (TextContent | ImageContent)[] = [];
            for (const group of plan) {
                const groupDesignCount = group.strips.reduce((n, strip) => n + strip.length, 0);
                resultContent.push({
                    type: 'text',
                    text: `### ${group.groupName} (${groupDesignCount} design${
                        groupDesignCount === 1 ? '' : 's'
                    })`,
                });
                const totalStrips = group.strips.length;
                for (let i = 0; i < group.strips.length; i++) {
                    const strip = group.strips[i];
                    const dataUrls = strip.map((design) => renderMap.get(design.id) ?? null);
                    const panels: StripPanel[] = strip.map((design, index) => ({
                        displayName: design.display_name,
                        sceneDesignId: design.id,
                        rendered: dataUrls[index] != null,
                    }));
                    const stripImage = await compositeTileStrip(dataUrls, aspectRatio);
                    resultContent.push({
                        type: 'text',
                        text: buildStripPreamble(panels, i + 1, totalStrips),
                    });
                    resultContent.push({ type: 'image_url', image_url: { url: stripImage } });
                }
            }

            const result = `Rendered ${designs.length} design${
                designs.length === 1 ? '' : 's'
            } across ${plan.length} group${plan.length === 1 ? '' : 's'} as tile-strip previews.`;

            return { isSuccess: true, result, resultContent };
        } catch (error) {
            if (error instanceof TemplateBundleNotLoadedError) {
                return {
                    result: `Scene design previews can only be rendered for the currently loaded template. Template "${templateId}" is not loaded, so there is nothing to preview. If you are creating a new video, call ${TOOL_NAMES.createBlankVideo} (with this template) first, then list the designs.`,
                    isSuccess: false,
                };
            }

            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

            // Parse specific error types and provide actionable feedback
            const errorLower = errorMessage.toLowerCase();

            if (errorLower.includes('template does not exist') || errorLower.includes('404')) {
                return {
                    result: `Error: Template "${templateId}" does not exist in the system.

Suggestions:
1. Use the ${TOOL_NAMES.findTemplates} tool first to find available templates (e.g., ${TOOL_NAMES.findTemplates}(search_term="modern professional"))
2. Try a different template_id parameter (common templates: "shamrock", "modern", "minimal")
3. If no template_id was provided, make sure a template is loaded in the video

Parameters used: template_id="${templateId}", aspect_ratio="${aspectRatio}", narrative_thread="${narrativeThread}"`,
                    isSuccess: false,
                };
            }

            if (errorLower.includes('aspect_ratio')) {
                return {
                    result: `Error: Invalid aspect_ratio "${aspectRatio}".

Valid aspect ratios: "landscape", "square", "portrait" (or "vertical")

Try calling ${TOOL_NAMES.listSceneDesigns} again with a valid aspect_ratio parameter.`,
                    isSuccess: false,
                };
            }

            // Generic error with context
            return {
                result: `Error listing scene designs: ${errorMessage}

Parameters used: template_id="${templateId}", aspect_ratio="${aspectRatio}", narrative_thread="${narrativeThread}"

Suggestion: Try using ${TOOL_NAMES.findTemplates} first to find available templates, or check if the template is properly loaded.`,
                isSuccess: false,
            };
        }
    }
}
